import { getCurrentUser } from "@nextcloud/auth";
import axios, { isAxiosError } from "@nextcloud/axios";
import { generateUrl } from "@nextcloud/router";
import { spawnDialog } from "@nextcloud/vue/functions/dialog";
import { computed, createBlock, createElementBlock, createElementVNode, createVNode, defineComponent, nextTick, onMounted, openBlock, ref, toDisplayString, unref, useTemplateRef, withCtx } from "vue";
import NcDialog from "@nextcloud/vue/components/NcDialog";
import NcPasswordField from "@nextcloud/vue/components/NcPasswordField";
import { getLoggerBuilder } from "@nextcloud/logger";
import { getGettextBuilder } from "@nextcloud/l10n/gettext";
//#region src/errors.ts
/*!
* SPDX-FileCopyrightText: 2026 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
/**
* Error thrown when the user cancelled the password confirmation,
* e.g. by closing the password dialog.
*/
var PasswordConfirmationCancelledError = class extends Error {
	constructor() {
		super("Password confirmation was cancelled");
		this.name = "PasswordConfirmationCancelledError";
	}
};
//#endregion
//#region src/globals.ts
/*!
* SPDX-FileCopyrightText: 2022 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
/**
* Lax: Confirm password if needed.
* Strict: Confirm in the request.
*/
var PwdConfirmationMode = /* @__PURE__ */ function(PwdConfirmationMode) {
	PwdConfirmationMode["Lax"] = "lax";
	PwdConfirmationMode["Strict"] = "strict";
	return PwdConfirmationMode;
}({});
//#endregion
//#region src/is-required.ts
/*!
* SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
var PAGE_LOAD_TIME = Date.now();
/**
* Check if password confirmation is required according to the last confirmation time.
* Use as a replacement of deprecated `OC.PasswordConfirmation.requiresPasswordConfirmation()`.
* Not needed if `confirmPassword()` can be used, because it checks requirements itself.
*
* @param mode - The confirmation mode for which to check the requirement
* @return Whether password confirmation is required or was confirmed recently
*/
function isPasswordConfirmationRequired(mode) {
	if (!window.backendAllowsPasswordConfirmation) return false;
	if (mode === PwdConfirmationMode.Strict) return true;
	const serverTimeDiff = PAGE_LOAD_TIME - window.nc_pageLoad * 1e3;
	return Date.now() - (serverTimeDiff + window.nc_lastLogin * 1e3) > 18e5;
}
//#endregion
//#region src/utils/logger.ts
/*!
* SPDX-FileCopyrightText: 2025 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: CC0-1.0
*/
var logger = getLoggerBuilder().setApp("@nextcloud/password-confirmation").detectLogLevel().build();
//#endregion
//#region src/apiError.ts
/*!
* SPDX-FileCopyrightText: 2026 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
var [NC_MAJOR_VERSION] = window._oc_config?.version.split(".").map(Number) ?? [];
/**
* Check if the given error is a confirmation error,
* which means that the password was incorrect.
*
* @param error - The error to check
*/
function isConfirmationError(error) {
	if (!isAxiosError(error) || !error.response) return false;
	if (NC_MAJOR_VERSION < 32) {
		logger.debug("Handle legacy confirmation error based on status code", { status: error.response.status });
		return error.response.status === 403;
	}
	const hasConfirmationHeader = error.response.headers?.["X-NC-Auth-NotConfirmed"] === "true";
	logger.debug("Handle modern confirmation error based on header", { hasConfirmationHeader });
	return hasConfirmationHeader;
}
//#endregion
//#region src/utils/l10n.ts
/*!
* SPDX-FileCopyrightText: 2022 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
var gtBuilder = getGettextBuilder().detectLanguage();
[
	{
		"locale": "af",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Verifikasie vereis"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Wagwoord word nagegaan ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bevestig"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Wagwoord"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Voer asseblief wagwoord in"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Hierdie handeling vereis verifikasie. Bevestig dit asseblief deur jou wagwoord in te voer."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Verkeerde wagwoord"]
			}
		]
	},
	{
		"locale": "ar",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["المصادقة مطلوبة"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["جار التحقق من كلمة المرور\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["تأكيد"]
			},
			{
				"msgid": "Password",
				"msgstr": ["كلمة المرور"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["يرجى إدخال كلمة المرور الخاصة بك"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["يتطلب هذا الإجراء المصادقة، يرجى تأكيده بإدخال كلمة المرور."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["كلمة المرور غير صحيحة"]
			}
		]
	},
	{
		"locale": "ast",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["Comprobando la contraseña…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmación"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Confirma la contraseña"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contraseña"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Introduz la contraseña"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Esta aición precisa l'autenticación"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["La contraseña ye incorreuta"]
			}
		]
	},
	{
		"locale": "az",
		"translations": [
			{
				"msgid": "Confirm",
				"msgstr": ["Təsdiq edin"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Parolunuzu təsdiq edin"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Parol"]
			},
			{
				"msgid": "Password confirmation dialog already mounted",
				"msgstr": ["Parolun təsdiqi dialoqu artıq quraşdırılıb"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Bu əməliyyat autentifikasiya tələb edir"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Səhv parol"]
			}
		]
	},
	{
		"locale": "be",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["Праверка пароля…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Пацвердзіць"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Пацвердзіць пароль"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Пароль"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Калі ласка, увядзіце ваш пароль"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Гэта дзеянне патрабуе аўтэнтыфікацыі"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Памылковы пароль"]
			}
		]
	},
	{
		"locale": "ca",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Cal autenticació"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["S'està comprovant la contrasenya\xA0..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirma"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contrasenya"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Si us plau, confirmeu la vostra contrasenya per continuar."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Introduïu la vostra contrasenya"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Aquesta acció requereix autenticació, si us plau, confirmeu-la introduint la teva contrasenya."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Contrasenya incorrecta"]
			}
		]
	},
	{
		"locale": "cs_CZ",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Vyžadováno ověření se"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Ověřování hesla…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Potvrdit"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Heslo"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Zadejte heslo"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Tato akce vyžaduje ověření se – potvrďte ji zadáním svého hesla."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Nesprávné heslo"]
			}
		]
	},
	{
		"locale": "da",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Bekræft din identitet"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Kontrollerer adgangskode …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bekræft"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Adgangskode"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Bekræft din adgangskode for at fortsætte."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Indtast din adgangskode"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Denne handling kræver godkendelse. Indtast din adgangskode for at bekræfte."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Forkert adgangskode"]
			}
		]
	},
	{
		"locale": "de",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Authentifizierung erforderlich"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Passwort prüfen \xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bestätigen"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Passwort"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Bitte bestätigen Sie Ihr Passwort, um fortzufahren."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Bitte gib dein Passwort ein"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Für diese Aktion ist eine Authentifizierung erforderlich. Bitte bestätige diese durch Eingabe deines Passworts."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Falsches Passwort"]
			}
		]
	},
	{
		"locale": "de_DE",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Authentifizierung erforderlich"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Passwort prüfen\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bestätigen"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Passwort"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Bitte bestätigen Sie Ihr Passwort, um fortzufahren."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Bitte geben Sie Ihr Passwort ein"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Für diese Aktion ist eine Authentifizierung erforderlich. Bitte bestätigen Sie diese durch Eingabe Ihres Passworts."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Falsches Passwort"]
			}
		]
	},
	{
		"locale": "el",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Απαιτείται πιστοποίηση"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Έλεγχος κωδικού πρόσβασης\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Επιβεβαίωση"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Συνθηματικό"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Παρακαλώ εισάγετε το συνθηματικό σας"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Αυτή η ενέργεια απαιτεί πιστοποίηση. Επιβεβαιώστε την εισάγοντας τον κωδικό πρόσβασής σας."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Λάθος συνθηματικό"]
			}
		]
	},
	{
		"locale": "en_GB",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Authentication required"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Checking password\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirm"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Password"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Please enter your password"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["This action needs authentication, please confirm it by entering your password."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Wrong password"]
			}
		]
	},
	{
		"locale": "es",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Se requiere autenticación"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Verificando contraseña\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contraseña"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Por favor, Introduzca su contraseña"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Esta acción requiere autenticación, por favor, confírmela ingresado su contraseña."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Contraseña errónea"]
			}
		]
	},
	{
		"locale": "es_AR",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["Verificando contraseña …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Confirme su contraseña"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contraseña"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Por favor, introduzca su contraseña"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Esta acción necesita autenticación"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Contraseña incorrecta"]
			}
		]
	},
	{
		"locale": "es_CO",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autenticación requerida"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Verificando contraseña …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contraseña"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Por favor introduzca su contraseña"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Esta acción necesita autentificación, por favor confírmela introduciendo su contraseña."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Contraseña incorrecta"]
			}
		]
	},
	{
		"locale": "es_MX",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["Verificando contraseña …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Confirme su contraseña"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contraseña"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Por favor introduzca su contraseña"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Esta acción necesita autenticación"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Contraseña incorrecta"]
			}
		]
	},
	{
		"locale": "et_EE",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autentimine on vajalik"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Kontrollin salasõna…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Kinnita"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Salasõna"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Jätkamiseks palun kinnita oma salasõna."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Palun sisesta oma salasõna"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["See tegevus eeldab autentimist, palun tee seda sisestades oma salasõna."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Vale salasõna"]
			}
		]
	},
	{
		"locale": "fa",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["احراز هویت مورد نیاز است"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["در حال بررسی رمز عبور..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["تأیید"]
			},
			{
				"msgid": "Password",
				"msgstr": ["رمز عبور"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["لطفاً رمز عبور خود را وارد کنید"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["این عمل نیاز به احراز هویت دارد، لطفاً با وارد کردن رمز عبور خود آن را تأیید کنید."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["رمز عبور اشتباه است"]
			}
		]
	},
	{
		"locale": "fi_FI",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Tunnistautuminen vaaditaan"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Tarkistetaan salasanaa\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Vahvista"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Salasana"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Kirjoita salasanasi"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Tämä toimenpide vaatii tunnistautumisen. Vahvista kirjoittamalla salasanasi."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Väärä salasana"]
			}
		]
	},
	{
		"locale": "fr",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Authentification requise"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Vérification du mot de passe…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmer"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Mot de passe"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Veuillez confirmer votre mot de passe pour poursuivre."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Veuillez saisir votre mot de passe"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Cette action nécessite une authentification. Veuillez confirmer cette action en saisissant votre mot de passe."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Mot de passe incorrect"]
			}
		]
	},
	{
		"locale": "ga",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Fíordheimhniú ag teastáil"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Ag seiceáil an focal faire\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Deimhnigh"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Pasfhocal"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Cuir isteach do phasfhocal"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Teastaíonn fíordheimhniú don ghníomh seo, deimhnigh é trí do phasfhocal a iontráil."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Pasfhocal mícheart"]
			}
		]
	},
	{
		"locale": "gl",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["É necesaria a autenticación"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Comprobando o contrasinal…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Contrasinal"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Confirme o contrasinal para proseguir."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Introduza o seu contrasinal"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Esta acción precisa de autenticación; confírmea introducindo o seu contrasinal."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Contrasinal incorrecto"]
			}
		]
	},
	{
		"locale": "hr",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Potrebna je autentifikacija"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Provjera lozinke\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Potvrdi"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Lozinka"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Molimo unesite vašu lozinku"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Ova radnja zahtijeva autentifikaciju, potvrdite je unosom svoje lozinke."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Pogrešna lozinka"]
			}
		]
	},
	{
		"locale": "hu_HU",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Hitelesítés szükséges"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Jelszó ellenőrzése…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Jóváhagyás"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Jelszó"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Adja meg a jelszavát"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Ehhez a tevékenységhez hitelesítés szükséges, erősítse meg a jelszava megadásával."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Hibás jelszó"]
			}
		]
	},
	{
		"locale": "id",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autentikasi diperlukan"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Memeriksa kata sandi ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Konfirmasi"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Kata sandi"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Silahkan masukan kata sandi Anda"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Aksi ini memerlukan autentikasi, silahkan konfirmasi dengan memasukan kata sandi Anda."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Kata sandi salah"]
			}
		]
	},
	{
		"locale": "is",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Auðkenningar krafist"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Athuga lykilorð …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Staðfesta"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Lykilorð"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Settu inn lykilorðið þitt"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Þessi aðgerð krefst auðkenningar, staðfestu hana með því að setja inn lykilorðið þitt."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Rangt lykilorð"]
			}
		]
	},
	{
		"locale": "it",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autenticazione richiesta"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Controllo della password…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Conferma"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Password"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Digita la tua password"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Questa azione richiede l'autenticazione, confermala digitando la tua password."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Password errata"]
			}
		]
	},
	{
		"locale": "ja_JP",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["認証が必要です"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["パスワードの確認\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["確認"]
			},
			{
				"msgid": "Password",
				"msgstr": ["パスワード"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["パスワードを入力してください"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["この操作には認証が必要です。パスワードを入力してください。"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["間違ったパスワード"]
			}
		]
	},
	{
		"locale": "kab",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Asesteb yettwasra"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Asenqed n wawal n uɛeddi\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Sentem"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Awal n uɛeddi"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Txil, sekcem-d awal-ik·im n uɛeddi"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Tigawt-a tesra asesteb, ttxil-k·m, wekked-itt-id s usekcem n wawal-inek·inem n uɛeddi."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Awal n uɛeddi d arameɣtu"]
			}
		]
	},
	{
		"locale": "ko",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["인증 필요 "]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["비밀번호\xA0확인 중 ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["확인"]
			},
			{
				"msgid": "Password",
				"msgstr": ["비밀번호"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["비밀번호를 입력하세요"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["이 작업은 인증이 필요합니다. 비밀번호를 입력하여 확인해주세요."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["잘못된 비밀번호"]
			}
		]
	},
	{
		"locale": "lb",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Authentifizéierung erfuerderlech"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Passwuert préiwen …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bestätegen"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Passwuert"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Wannechgelift gëff däi Passwuert an"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Fir dës Aktioun ass eng Authentifizéierung erfuerderlech. Wannechgelift bestäteg dës duerch d'Aginn vun dengem Passwuert."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Falscht Passwuert"]
			}
		]
	},
	{
		"locale": "lo",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["ຈຳເປັນຕ້ອງມີການຢືນຢັນຕົວຕົນ"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["ກຳລັງກວດສອບລະຫັດຜ່ານ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["ຢືນຢັນ"]
			},
			{
				"msgid": "Password",
				"msgstr": ["ລະຫັດຜ່ານ"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["ກະລຸນາປ້ອນລະຫັດຜ່ານຂອງທ່ານ"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["ການດຳເນີນການນີ້ຈຳເປັນຕ້ອງມີການຢືນຢັນຕົວຕົນ, ກະລຸນາຢືນຢັນໂດຍການປ້ອນລະຫັດຜ່ານຂອງທ່ານ."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ"]
			}
		]
	},
	{
		"locale": "lt_LT",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Reikalingas autentifikavimas"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Tikrinamas slaptažodis \xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Patvirtinti"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Slaptažodis"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Įveskite slaptažodį"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Šiam veiksmui reikalinga autentifikacija, patvirtinkite ją įvesdami slaptažodį."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Neteisingas slaptažodis"]
			}
		]
	},
	{
		"locale": "lv",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Nepieciešama autentificēšanās"]
			},
			{
				"msgid": "Checking password …",
				"msgstr": ["Pārbauda paroli..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Apstiprināt"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Parole"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Lūgums ievadīt savu paroli"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Šai darbībai ir nepieciešama autentificēšanās. Lūgums to apstiprināt ar savas paroles ievadīšanu."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Nepareiza parole"]
			}
		]
	},
	{
		"locale": "mk",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Потребна е автентификација"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Проверка на лозинка\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Потврди"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Лозинка"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Внесете ја вашата лозинка"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Оваа акција бара автентикација, потврдете ја со внесување на вашата лозинка."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Погрешна лозинка"]
			}
		]
	},
	{
		"locale": "ms_MY",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Pengesahan diperlukan"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Menyemak kata laluan\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Mengesahkan"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Kata laluan"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Sila masukkan kata laluan anda"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Tindakan ini memerlukan pengesahan, sila sahkan dengan memasukkan kata laluan anda."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Kata laluan salah"]
			}
		]
	},
	{
		"locale": "nb_NO",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autentisering påkrevd"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Sjekker passordet\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bekreft"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Passord"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Vennligst skriv inn passordet ditt"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Denne handlingen krever autentisering; vennligst angi ditt passord."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Feil passord"]
			}
		]
	},
	{
		"locale": "nl",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Authenticatie vereist"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Wachtwoord controleren…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bevestigen"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Wachtwoord"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Voer je wachtwoord in"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Voor deze handeling is authenticatie vereist, bevestig deze door je wachtwoord in te voeren."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Incorrect wachtwoord"]
			}
		]
	},
	{
		"locale": "pl",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["Sprawdzanie hasła…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Potwierdź"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Potwierdź swoje hasło"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Hasło"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Wprowadź swoje hasło"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Wykonanie tej czynności wymaga autoryzacji"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Nieprawidłowe hasło"]
			}
		]
	},
	{
		"locale": "pt_BR",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autenticação necessária"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Verificando a senha\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Senha"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Por favor, confirme sua senha para continuar."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Por favor, insira sua senha"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Esta ação precisa de autenticação. Por favor, confirme-a digitando sua senha."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Senha incorreta"]
			}
		]
	},
	{
		"locale": "pt_PT",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autenticação necessária"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["A verificar palavra-passe…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmar"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Palavra-passe"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Introduza a sua palavra-passe, por favor"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Esta ação requer autenticação; confirme-a introduzindo a sua palavra-passe."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Palavra-passe incorreta"]
			}
		]
	},
	{
		"locale": "ro",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["Se verifică parola ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Confirmare"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["Confirmare parolă"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Parolă"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Vă rugăm să introduceți parola"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["Această acțiune necesită autentificare"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Parolă incorectă"]
			}
		]
	},
	{
		"locale": "ru",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Требуется аутентификация"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Проверка пароля\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Подтвердить"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Пароль"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["Подтвердите пароль, чтобы продолжить."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Введите свой пароль"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Для этого действия нужна аутентификация: подтвердите его паролем."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Неправильный пароль"]
			}
		]
	},
	{
		"locale": "sk_SK",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Vyžaduje sa autentifikácia"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Kontrola hesla…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Potvrdiť"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Heslo"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Zadajte prosím svoje heslo"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Táto akcia vyžaduje overenie, prosím potvrďte ju zadaním vášho hesla."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Nesprávne heslo"]
			}
		]
	},
	{
		"locale": "sl",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Zahtevana avtentikacija"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Poteka preverjanje gesla ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Potrdi"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Geslo"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Vpisati je treba geslo"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["To dejanje zahteva preverjanje pristnosti, potrdite ga z vnosom gesla."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Napačno geslo"]
			}
		]
	},
	{
		"locale": "sr",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Потребна је потврда идентитета"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Проверава се лозинка…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Потврда"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Лозинка"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Молимо вас да унесете своју лозинку"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["За ову акцију је потребна провера идентитета, молимо вас да га потврдите уносом своје лозинке."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Погрешна лозинка"]
			}
		]
	},
	{
		"locale": "sv",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autentisering krävs"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Kontrollerar lösenordet\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Bekräfta"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Lösenord"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Ange ditt lösenord"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Den här åtgärden kräver autentisering. Bekräfta den genom att ange ditt lösenord."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Fel lösenord"]
			}
		]
	},
	{
		"locale": "tr",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Kimlik doğrulaması gerekli"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Parola denetleniyor…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Parola onayı"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Parola"]
			},
			{
				"msgid": "Please confirm your password to proceed.",
				"msgstr": ["İlerlemek için lütfen parolanızı doğrulayın."]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Lütfen parolanızı yazın"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Bu işlemi yapmak için kimliğinizi doğrulamalısınız. Lütfen parolanızı yazın."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Parola geçersiz"]
			}
		]
	},
	{
		"locale": "uk",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Потрібна авторизація"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Перевірка паролю ..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Підтвердити"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Пароль"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Зазначте ваш пароль"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Ця дія вимагає авторизацію, зазначте ваш пароль."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Помилковий пароль"]
			}
		]
	},
	{
		"locale": "ur_PK",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["تصدیق درکار ہے"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["پاس ورڈ چیک ہو رہا ہے …"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["تصدیق کریں"]
			},
			{
				"msgid": "Password",
				"msgstr": ["پاس ورڈ"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["براہ کرم اپنا پاس ورڈ درج کریں"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["اس عمل کو تصدیق کی ضرورت ہے، براہ کرم پاس ورڈ درج کرکے اس کی تصدیق کریں۔"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["نادرست پاس ورڈ"]
			}
		]
	},
	{
		"locale": "uz",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Autentifikatsiya talab qilinadi"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Parol tekshirilmoqda…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Tasdiqlang"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Parol"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Iltimos, parolingizni kiriting"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Ushbu amaliyot autentifikatsiya talab etadi, parolingizni kiritish orqali buni tasdiqlang."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Parol noto'g'ri "]
			}
		]
	},
	{
		"locale": "vi",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["Yêu cầu xác thực"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["Đang kiểm tra mật khẩu\xA0..."]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["Chấp nhận"]
			},
			{
				"msgid": "Password",
				"msgstr": ["Mật khẩu"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["Vui lòng nhập mật khẩu của bạn"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["Thao tác này cần xác thực, vui lòng xác nhận bằng cách nhập mật khẩu của bạn."]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["Mật khẩu sai"]
			}
		]
	},
	{
		"locale": "zh_CN",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["需要身份验证"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["正在检查密码\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["确认"]
			},
			{
				"msgid": "Password",
				"msgstr": ["密码"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["请输入您的密码"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["此操作需要身份验证，请输入密码进行确认。"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["密码错误"]
			}
		]
	},
	{
		"locale": "zh_HK",
		"translations": [
			{
				"msgid": "Authentication required",
				"msgstr": ["需要驗證"]
			},
			{
				"msgid": "Checking password\xA0…",
				"msgstr": ["正在檢查密碼\xA0…"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["確認"]
			},
			{
				"msgid": "Password",
				"msgstr": ["密碼"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["請輸入您的密碼"]
			},
			{
				"msgid": "This action needs authentication, please confirm it by entering your password.",
				"msgstr": ["此操作需要身份驗證，請輸入您的密碼以進行確認。"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["密碼錯誤"]
			}
		]
	},
	{
		"locale": "zh_TW",
		"translations": [
			{
				"msgid": "Checking password …",
				"msgstr": ["正在檢查密碼……"]
			},
			{
				"msgid": "Confirm",
				"msgstr": ["確認"]
			},
			{
				"msgid": "Confirm your password",
				"msgstr": ["確認您的密碼"]
			},
			{
				"msgid": "Password",
				"msgstr": ["密碼"]
			},
			{
				"msgid": "Please enter your password",
				"msgstr": ["請輸入您的密碼"]
			},
			{
				"msgid": "This action needs authentication",
				"msgstr": ["此動作需要驗證"]
			},
			{
				"msgid": "Wrong password",
				"msgstr": ["密碼錯誤"]
			}
		]
	}
].map(({ locale, translations }) => gtBuilder.addTranslation(locale, {
	headers: {},
	translations: { "": Object.fromEntries(translations.map((t) => [t.msgid, t])) }
}));
var gt = gtBuilder.build();
gt.ngettext.bind(gt);
var t = gt.gettext.bind(gt);
//#endregion
//#region src/components/PasswordDialog.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { key: 0 };
var _hoisted_2 = { key: 1 };
var PasswordDialog_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "PasswordDialog",
	props: {
		customText: {},
		validate: { type: Function }
	},
	emits: ["close"],
	setup(__props, { emit: __emit }) {
		const props = __props;
		const emit = __emit;
		onMounted(focusPasswordField);
		const passwordInput = useTemplateRef("field");
		const password = ref("");
		const loading = ref(false);
		const hasError = ref(false);
		const buttons = [{
			label: t("Confirm"),
			type: "submit",
			variant: "primary",
			callback
		}];
		const helperText = computed(() => {
			if (hasError.value) return t("Wrong password");
			if (loading.value) return t("Checking password\xA0…");
			if (password.value === "") return t("Please enter your password");
			return "";
		});
		/**
		* Handle confirm button click
		*/
		async function callback() {
			hasError.value = false;
			loading.value = true;
			if (password.value === "") {
				hasError.value = true;
				return false;
			}
			try {
				await props.validate(password.value);
				emit("close", true);
			} catch (error) {
				if (isConfirmationError(error)) {
					hasError.value = true;
					logger.error("Exception during password confirmation", { error });
					selectPasswordField();
					return false;
				}
				hasError.value = true;
				emit("close", false);
			} finally {
				loading.value = false;
			}
			return true;
		}
		/**
		* Focus the password field
		*/
		function focusPasswordField() {
			nextTick(() => {
				passwordInput.value.focus();
			});
		}
		/**
		* Select the password field
		*/
		function selectPasswordField() {
			nextTick(() => {
				passwordInput.value.select();
			});
		}
		return (_ctx, _cache) => {
			return openBlock(), createBlock(unref(NcDialog), {
				isForm: "",
				buttons,
				name: unref(t)("Authentication required"),
				contentClasses: _ctx.$style.passwordDialog,
				"onUpdate:open": _cache[1] || (_cache[1] = ($event) => emit("close", false))
			}, {
				default: withCtx(() => [__props.customText ? (openBlock(), createElementBlock("div", _hoisted_1, [createElementVNode("p", null, toDisplayString(__props.customText), 1), createElementVNode("p", null, toDisplayString(unref(t)("Please confirm your password to proceed.")), 1)])) : (openBlock(), createElementBlock("p", _hoisted_2, toDisplayString(unref(t)("This action needs authentication, please confirm it by entering your password.")), 1)), createVNode(unref(NcPasswordField), {
					ref: "field",
					modelValue: password.value,
					"onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => password.value = $event),
					label: unref(t)("Password"),
					helperText: helperText.value,
					checkPasswordStrength: false,
					error: hasError.value !== false,
					required: ""
				}, null, 8, [
					"modelValue",
					"label",
					"helperText",
					"error"
				])]),
				_: 1
			}, 8, ["name", "contentClasses"]);
		};
	}
});
var PasswordDialog_vue_vue_type_style_index_0_lang_module_default = { passwordDialog: "_passwordDialog_joix2_2" };
//#endregion
//#region \0plugin-vue:export-helper
var _plugin_vue_export_helper_default = (sfc, props) => {
	const target = sfc.__vccOpts || sfc;
	for (const [key, val] of props) target[key] = val;
	return target;
};
var PasswordDialog_default = /*#__PURE__*/ _plugin_vue_export_helper_default(PasswordDialog_vue_vue_type_script_setup_true_lang_default, [["__cssModules", { "$style": PasswordDialog_vue_vue_type_style_index_0_lang_module_default }]]);
//#endregion
//#region src/password-confirmation.ts
/*!
* SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
var INTERCEPTOR_INITIALIZED = false;
/**
* Confirm password if needed.
* Replacement of deprecated `OC.PasswordConfirmation.requirePasswordConfirmation(callback)`
*
* @param options - Additional options
* @param options.text - Custom text to show in the dialog, instead of the default text
* @return Promise that resolves when password is confirmed or not needed.
*                         Rejects with `PasswordConfirmationCancelledError` if password confirmation was cancelled.
*/
async function confirmPassword(options = {}) {
	if (!isPasswordConfirmationRequired(PwdConfirmationMode.Lax)) return Promise.resolve();
	await promptConfirmedPassword(options.text);
}
/**
* Prompt the user for the password and validate it using the confirmation endpoint.
*
* @param text - Custom text to show in the dialog, instead of the default text
* @return The confirmed password
*/
async function promptConfirmedPassword(text) {
	let password = "";
	await promptPassword(async (value) => {
		await _confirmPassword(value);
		password = value;
	}, text);
	return password;
}
/**
* Get the Basic Auth credentials of the current user.
*
* @param password - The password of the current user
*/
function getBasicAuth(password) {
	return {
		username: getCurrentUser()?.uid ?? "",
		password
	};
}
/**
* @param password - Password to be confirmed
*/
async function _confirmPassword(password) {
	logger.debug("Confirming password");
	const url = generateUrl("/login/confirm");
	const { data } = await axios.post(url, { password });
	window.nc_lastLogin = data.lastLogin;
	logger.debug("Password confirmed");
}
var _passwordDialog;
var _dialogCallback;
/**
* Spawn a dialog to prompt the password.
*
* @param validate Is called to validate the user's password
* @param customText Optional custom text to show in the dialog, instead of the default text
*/
async function promptPassword(validate, customText) {
	_dialogCallback = validate;
	if (!_passwordDialog) _passwordDialog = spawnDialog(PasswordDialog_default, {
		customText,
		validate(password) {
			return _dialogCallback(password);
		}
	});
	const result = await _passwordDialog;
	_passwordDialog = void 0;
	if (!result) throw new PasswordConfirmationCancelledError();
}
/**
* Group multiple requests that require strict password confirmation,
* so the user only needs to enter the password once.
*
* The password is validated once before the callback is executed,
* then all requests made with the axios instance passed to the callback
* are authenticated with the confirmed password.
*
* @example
* ```ts
* await withStrictPasswordConfirmation(async (axios) => {
*     await axios.put(...)
*     await axios.get(...)
* }, { text: t('myapp', 'The following apps will be updated …') })
* ```
*
* @param callback - Function performing the requests using the provided axios instance
* @param options - Additional options
* @param options.text - Custom text to show in the dialog, instead of the default text
* @param options.axios - The axios instance to use, defaults to `@nextcloud/axios`
* @return Promise resolving to the return value of the callback.
*                         Rejects with `PasswordConfirmationCancelledError` if password confirmation was cancelled.
*/
async function withStrictPasswordConfirmation(callback, options = {}) {
	const client = options.axios ?? axios;
	if (!isPasswordConfirmationRequired(PwdConfirmationMode.Strict)) return await callback(client);
	return await callback(createAuthenticatedClient(client, getBasicAuth(await promptConfirmedPassword(options.text))));
}
/**
* Create a proxy of an axios instance that adds the auth information to every request.
* Using a proxy ensures that unrelated requests made with the same instance are not affected,
* while defaults and interceptors of the original instance are still used.
*
* @param client - The axios instance to wrap
* @param auth - The auth information to add to the requests
*/
function createAuthenticatedClient(client, auth) {
	const withAuth = (config = {}) => ({
		...config,
		auth,
		confirmPassword: void 0
	});
	return new Proxy(client, {
		apply(target, thisArg, [urlOrConfig, config]) {
			return typeof urlOrConfig === "string" ? target(urlOrConfig, withAuth(config)) : target(withAuth(urlOrConfig));
		},
		get(target, property, receiver) {
			switch (property) {
				case "request": return (config) => target.request(withAuth(config));
				case "get":
				case "delete":
				case "head":
				case "options": return (url, config) => target[property](url, withAuth(config));
				case "post":
				case "put":
				case "patch":
				case "postForm":
				case "putForm":
				case "patchForm": return (url, data, config) => target[property](url, data, withAuth(config));
				default: return Reflect.get(target, property, receiver);
			}
		}
	});
}
/**
* Add axios interceptors to an axios instance that will ask for
* password confirmation to add it as Basic Auth for every requests.
*
* @param axios - The axios instance to add intercepters to
*/
function addPasswordConfirmationInterceptors(axios) {
	if (INTERCEPTOR_INITIALIZED) return;
	INTERCEPTOR_INITIALIZED = true;
	let validatePromise;
	axios.interceptors.request.use(async (config) => {
		if (config.confirmPassword === void 0) return config;
		if (!isPasswordConfirmationRequired(config.confirmPassword)) return config;
		if (config.confirmPassword === PwdConfirmationMode.Lax) {
			await promptConfirmedPassword();
			return config;
		}
		const { promise, resolve, reject } = Promise.withResolvers();
		promptPassword(async (password) => {
			validatePromise = Promise.withResolvers();
			config.auth = getBasicAuth(password);
			logger.debug("Adding auth info to the request", { config });
			resolve(config);
			return validatePromise.promise;
		}).catch(reject);
		return promise;
	});
	axios.interceptors.response.use((response) => {
		if (response.config.confirmPassword !== PwdConfirmationMode.Strict) return response;
		if (validatePromise === void 0) {
			logger.debug("Password confirmation not required", { response });
			return response;
		}
		logger.debug("Password confirmation succeeded", { response });
		window.nc_lastLogin = Date.now() / 1e3;
		validatePromise.resolve();
		return response;
	}, (error) => {
		if (error.config?.confirmPassword !== PwdConfirmationMode.Strict) throw error;
		if (validatePromise === void 0) {
			logger.debug("Password confirmation not required", { error });
			throw error;
		}
		logger.debug("Password confirmation failed", { error });
		validatePromise.reject(error);
		if (isConfirmationError(error)) {
			logger.debug("Triggering new request", { error });
			return axios.request(error.config);
		}
		throw error;
	});
}
//#endregion
//#region src/index.ts
/*!
* SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
* SPDX-License-Identifier: MIT
*/
//#endregion
export { PasswordConfirmationCancelledError, PwdConfirmationMode, addPasswordConfirmationInterceptors, confirmPassword, isPasswordConfirmationRequired, withStrictPasswordConfirmation };

//# sourceMappingURL=index.mjs.map